<template>
  <div v-if="actions.length" style="display: contents">
    <v-menu v-model="visible" content-class="shell-pop" left min-width="260" nudge-bottom="8" offset-y transition="slide-y-transition"
            z-index="10">
      <template v-slot:activator="{ on, attrs }">
        <button aria-label="Neu anlegen" class="shell-new" type="button" v-bind="attrs" v-on="on">
          <v-icon small>mdi-plus</v-icon>
          <span v-if="$vuetify.breakpoint.mdAndUp">Neu</span>
        </button>
      </template>
      <div class="shell-pop__title">Neu anlegen</div>
      <v-list class="pt-0" dense>
        <v-list-item v-for="action in actions" :key="action.key" @click="open(action.key)">
          <v-list-item-icon>
            <v-icon dense>{{ action.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ action.text }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-menu>
    <!-- Dialogs are only mounted once they are used and opened right after mounting -->
    <create-institution-dialog v-if="mounted.includes('institution')" v-model="dialogs.institution"
                               @create:institution="(institution) => created('institution', institution)"
                               @hook:mounted="onDialogMounted('institution')"/>
    <create-parents-and-students-dialogs v-if="mounted.includes('student')" v-model="dialogs.student" :create-parents="false"
                                         @create:student="(student) => created('student', student)"
                                         @hook:mounted="onDialogMounted('student')"/>
    <create-employee-dialog v-if="mounted.includes('employee')" v-model="dialogs.employee" @create:employee="created('employee')"
                            @hook:mounted="onDialogMounted('employee')"/>
    <create-employee-dialog v-if="mounted.includes('applicant')" v-model="dialogs.applicant" for-applicants
                            @create:employee="created('applicant')"
                            @hook:mounted="onDialogMounted('applicant')"/>
    <create-training-dialog v-if="mounted.includes('training')" v-model="dialogs.training" @update="(training) => created('training', training, '/trainings')"
                            @hook:mounted="onDialogMounted('training')"/>
    <create-acquisition-list-dialog v-if="mounted.includes('acquisitionList')" v-model="dialogs.acquisitionList"
                                    @create:acquisitionList="created('acquisitionList', undefined, '/acquisitionLists')" @hook:mounted="onDialogMounted('acquisitionList')"/>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {getShellCreateActions, ShellCreateAction, ShellCreateKey} from '@/components/app/shell/shellCreateActions';
import {emitEntityCreated} from '@/helpers/entityCreatedEvents';

/** "Neu" button of the app bar: quick creation of the most common records from every page */
export default Vue.extend({
  components: {
    CreateInstitutionDialog: () => import('@/components/institutions/createInstitutionDialog.vue'),
    CreateParentsAndStudentsDialogs: () => import('@/components/common/createParentsAndStudentsDialogs.vue'),
    CreateEmployeeDialog: () => import('@/components/employees/createEmployeeDialog.vue'),
    CreateTrainingDialog: () => import('@/components/trainings/createTrainingDialog.vue'),
    CreateAcquisitionListDialog: () => import('@/components/acquisitionList/createAcquisitionListDialog.vue'),
  },
  data: () => ({
    visible: false,
    mounted: [] as ShellCreateKey[],
    dialogs: {
      institution: false,
      student: false,
      employee: false,
      applicant: false,
      training: false,
      acquisitionList: false,
    } as Record<ShellCreateKey, boolean>,
  }),
  computed: {
    actions(): ShellCreateAction[] {
      // The access manager in the store is replaced when the rights change (login, "login as")
      const accessManager = this.$store.state.crudAccessManager;
      return accessManager ? getShellCreateActions(accessManager) : [];
    },
  },
  methods: {
    open(key: ShellCreateKey) {
      this.visible = false;
      if (this.mounted.includes(key)) {
        this.dialogs[key] = true;
      } else {
        this.mounted = [...this.mounted, key];
      }
    },
    /** A freshly loaded dialog is opened once it is mounted, so it sees the change of its value */
    onDialogMounted(key: ShellCreateKey) {
      this.$nextTick(() => {
        this.dialogs[key] = true;
      });
    },
    /**
     * A dialog created a record: an open overview of that type reloads (it listens to the event),
     * for trainings and acquisition lists the overview is opened when another page is shown
     */
    created(key: ShellCreateKey, record?: { _id?: unknown }, overviewPath?: string) {
      emitEntityCreated(key, record?._id);
      if (overviewPath) {
        this.goTo(overviewPath);
      }
    },
    /** Shows the overview of the new record, unless it is already open */
    goTo(path: string) {
      if (this.$route.path !== path) {
        this.$router.push(path);
      }
    },
  },
});
</script>
